import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, Loader2, Lightbulb, Sparkles, CheckCircle2, Circle, FlaskConical, Rocket } from 'lucide-react';
import { roadmapApi } from '../api';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/dashboard/DashboardLayout';

const STATUS = {
  proposed: { color: 'bg-slate-100 text-slate-700', icon: Circle, key: 'status_proposed' },
  accepted: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle2, key: 'status_accepted' },
  in_beta: { color: 'bg-amber-100 text-amber-700', icon: FlaskConical, key: 'status_in_beta' },
  shipped: { color: 'bg-emerald-100 text-emerald-700', icon: Rocket, key: 'status_shipped' },
};

function IdeaCard({ idea, onVote }) {
  const { t } = useTranslation();
  const [voting, setVoting] = useState(false);
  const status = STATUS[idea.status] || STATUS.proposed;
  const StatusIcon = status.icon;

  const handleVote = async () => {
    if (voting) return;
    setVoting(true);
    try {
      await roadmapApi.vote(idea.id);
      onVote(idea.id);
    } catch (e) {
      console.error(e);
    } finally {
      setVoting(false);
    }
  };

  return (
    <article className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4 hover:shadow-sm transition">
      <button
        onClick={handleVote}
        disabled={voting}
        className={`flex flex-col items-center justify-center w-16 rounded-lg border-2 transition ${
          idea.i_voted ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-indigo-200'
        }`}
        title={idea.i_voted ? t('roadmap.vote_tooltip_remove') : t('roadmap.vote_tooltip_add')}
      >
        {voting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ThumbsUp className={`w-5 h-5 ${idea.i_voted ? 'fill-current' : ''}`} />}
        <span className="text-sm font-semibold mt-0.5">{idea.likes}</span>
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-slate-900 flex-1">{idea.summary}</h3>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${status.color} whitespace-nowrap`}>
            <StatusIcon className="w-3 h-3" /> {t(`roadmap.${status.key}`)}
          </span>
        </div>

        {idea.content && idea.content !== idea.summary && (
          <p className="text-sm text-slate-600 mb-2 line-clamp-2">{idea.content}</p>
        )}

        {idea.tags && idea.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {idea.tags.map((t, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">#{t}</span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

export default function RoadmapPage() {
  const { t } = useTranslation();
  const [data, setData] = useState({ shortlist: [], backlog: [], total_ideas: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await roadmapApi.list();
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleVoted = (ideaId) => {
    setData(prev => {
      const update = (list) => list.map(i => {
        if (i.id !== ideaId) return i;
        return { ...i, i_voted: !i.i_voted, likes: i.likes + (i.i_voted ? -1 : 1) };
      });
      return { ...prev, shortlist: update(prev.shortlist), backlog: update(prev.backlog) };
    });
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Lightbulb className="w-6 h-6 text-amber-500" /> {t('roadmap.title')}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          <Sparkles className="w-3 h-3 inline mr-1" />
          {t('roadmap.subtitle')}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : data.total_ideas === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{t('roadmap.empty')}</p>
        </div>
      ) : (
        <>
          {data.shortlist.length > 0 && (
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                {t('roadmap.top_n', { n: data.shortlist.length, total: data.total_ideas })}
              </h2>
              <div className="space-y-3">
                {data.shortlist.map(i => <IdeaCard key={i.id} idea={i} onVote={handleVoted} />)}
              </div>
            </section>
          )}

          {data.backlog.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                {t('roadmap.backlog', { n: data.backlog.length })}
              </h2>
              <div className="space-y-3 opacity-80">
                {data.backlog.map(i => <IdeaCard key={i.id} idea={i} onVote={handleVoted} />)}
              </div>
            </section>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
